import { Module, Lunch } from './schools.model';
import { Weekday } from './lunch.model';
import { StatusConfig } from './registration-status.model';
import { DocumentReference, Timestamp } from 'firebase/firestore';

// WARNING: A copy of this interface exists in functions/src/_shared/models.ts
// If you modify this interface, make sure to also update the copy in Firebase Functions!
export interface Alteration {
  id: string;
  type: AlterationType;
  status: AlterationStatus;
  studentId: string;
  studentRef: DocumentReference;
  studentFirstName: string;
  studentLastName: string;
  submittedBy: string;
  submittedByEmail: string;
  // UID des Eltern-Accounts am Kind (Student.userId) — bei Anträgen aus dem
  // Mitarbeiter-Portal weicht sie von submittedBy ab. Grundlage dafür, dass
  // Eltern solche Anträge sehen und widerrufen können (#866). Fehlt auf
  // Alt-Dokumenten vor 2026-08-30.
  studentUserId?: string;
  submittedOn: Timestamp;
  processedOn?: Timestamp;
  requestData: CancellationRequestData | ContractChangeRequestData | StudentDataCorrectionRequestData;
  schoolId: string;
  schoolRef: DocumentReference;
  schoolYear: number;
  comments?: string;
  internalNotes?: string;
  processedBy?: string | { uid: string; email: string };

  // Lifecycle timestamps
  appliedOn?: Timestamp; // When changes were applied to student record
  appliedImmediately?: boolean; // Flag to prevent double-application when applied immediately (not scheduled)
  cancelledOn?: Timestamp; // When user cancelled the alteration
  cancelledBy?: string; // User ID who cancelled
  /** Rücknahme durch Mitarbeitende nach Genehmigung oder Wirksamkeit (#1185). */
  withdrawal?: {
    reason: string;
    previousStatus: 'approved' | 'applied';
    withdrawnOn: Timestamp;
    withdrawnBy: { uid: string; email: string };
  };

  // Deprecated - no longer used (kept for backward compatibility)
  gracePeriodEndsOn?: Timestamp;
}

// WARNING: These types are also copied in functions/src/_shared/models.ts
export type AlterationType = 'cancellation' | 'contractChange' | 'studentDataCorrection' | 'registrationModuleChange';

// WARNING: This type is also copied in functions/src/_shared/models.ts
export type AlterationStatus =
  | 'submitted' // User submitted, awaiting approval
  | 'approved' // Approved, waiting for effectiveDate
  | 'applied' // Changes applied to student record
  | 'rejected' // Rejected by employee
  | 'cancelled'; // User cancelled before effectiveDate

// WARNING: This interface is also copied in functions/src/_shared/models.ts
export interface CancellationRequestData {
  reason: CancellationReason;
  customReason?: string;
  effectiveDate?: Timestamp;
  additionalNotes?: string;
  cancellationType: 'contract' | 'registration';
}

// WARNING: This interface is also copied in functions/src/_shared/models.ts (but with 'any' types)
export interface ContractChangeRequestData {
  currentModule: Module;
  requestedModule: Module;
  /** @deprecated Use currentLunchConfigurationId. Still written parallel for backward compatibility with in-flight alterations submitted before 2026-05. */
  currentLunch: Lunch | null;
  /** @deprecated Use requestedLunchConfigurationId. Still written parallel for backward compatibility. */
  requestedLunch: Lunch | null;
  /** Neu ab 2026-05: ID der aktuell hinterlegten LunchConfiguration (oder null wenn kein Mittagessen) */
  currentLunchConfigurationId?: string | null;
  /** Neu ab 2026-05: Nur bei DAILY_SELECTION-Config gesetzt */
  currentSelectedLunchDays?: Weekday[];
  /** Neu ab 2026-05: ID der gewünschten LunchConfiguration (oder null wenn kein Mittagessen) */
  requestedLunchConfigurationId?: string | null;
  /** Neu ab 2026-05: Nur bei DAILY_SELECTION-Config gesetzt */
  requestedSelectedLunchDays?: Weekday[];
  reason?: string;
  effectiveDate?: Timestamp;
  additionalNotes?: string;
}

// WARNING: This interface is also copied in functions/src/_shared/models.ts
export interface StudentDataCorrectionRequestData {
  /** Flache Feldpfade → neue Werte (z.B. { firstName: 'Max' }) */
  changedFields: Record<string, unknown>;
  /** Alte Werte für Anzeige/Revert */
  previousValues: Record<string, unknown>;
  additionalNotes?: string;
}

// WARNING: This type is also copied in functions/src/_shared/models.ts
export type CancellationReason =
  | 'moving'
  | 'schoolChange'
  | 'other';

export const CancellationReasonLabels: Record<CancellationReason, string> = {
  moving: $localize`:@@alteration.reason.moving:Umzug`,
  schoolChange: $localize`:@@alteration.reason.schoolChange:Schulwechsel`,
  other: $localize`:@@alteration.reason.other:Sonstiges`,
};

export const AlterationStatusConfigs: Record<AlterationStatus, StatusConfig> = {
  submitted: {
    type: 'info',
    label: $localize`:@@alteration.status.submitted:Eingereicht`,
    action: $localize`:@@alteration.status.submittedAction:Der Antrag wurde eingereicht und wird geprüft.`,
    styles: { background: 'var(--theme-primary)', color: '#fff' },
  },
  approved: {
    type: 'success',
    label: $localize`:@@alteration.status.approved:Genehmigt`,
    action: $localize`:@@alteration.status.approvedAction:Der Antrag wurde genehmigt und wird zum angegebenen Datum wirksam.`,
    styles: { background: 'var(--theme-success)', color: '#fff' },
  },
  applied: {
    type: 'success',
    label: $localize`:@@alteration.status.applied:Durchgeführt`,
    action: $localize`:@@alteration.status.appliedAction:Die Änderung wurde erfolgreich durchgeführt.`,
    styles: { background: 'var(--theme-success)', color: '#fff' },
  },
  rejected: {
    type: 'error',
    label: $localize`:@@alteration.status.rejected:Abgelehnt`,
    action: $localize`:@@alteration.status.rejectedAction:Der Antrag wurde abgelehnt.`,
    styles: { background: 'var(--theme-error)', color: '#fff' },
  },
  cancelled: {
    type: 'error',
    label: $localize`:@@alteration.status.cancelled:Storniert`,
    action: $localize`:@@alteration.status.cancelledAction:Die Änderung wurde vom Nutzer storniert.`,
    styles: { background: 'var(--theme-error)', color: '#fff' },
  },
};

export const AlterationStatusOptions = [
  { value: '', label: $localize`:@@alteration.status.all:Alle` },
  ...Object.entries(AlterationStatusConfigs).map(([value, config]) => ({
    value,
    label: config.label,
  })),
];

export const AlterationTypeLabels: Record<AlterationType, string> = {
  cancellation: $localize`:@@alteration.type.cancellation:Kündigung`,
  contractChange: $localize`:@@alteration.type.contractChange:Vertragsänderung`,
  studentDataCorrection: $localize`:@@alteration.type.studentDataCorrection:Datenkorrektur`,
  registrationModuleChange: $localize`:@@alteration.type.registrationModuleChange:Modulwechsel (Anmeldung)`,
};

// Type guards for discriminating request data types
// WARNING: These functions are also copied in functions/src/_shared/models.ts
export function isCancellationRequest(
  alteration: Alteration
): alteration is Alteration & { requestData: CancellationRequestData } {
  return alteration.type === 'cancellation';
}

export function isContractChangeRequest(
  alteration: Alteration
): alteration is Alteration & { requestData: ContractChangeRequestData } {
  return alteration.type === 'contractChange';
}

export function isStudentDataCorrectionRequest(
  alteration: Alteration
): alteration is Alteration & { requestData: StudentDataCorrectionRequestData } {
  return alteration.type === 'studentDataCorrection';
}

export function isRegistrationModuleChangeRequest(
  alteration: Alteration
): alteration is Alteration & { requestData: ContractChangeRequestData } {
  return alteration.type === 'registrationModuleChange';
}
